import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useLocation } from "react-router-dom";
import { MobXProviderContext } from "mobx-react";
import { Button } from "primereact/button";
import { confirmDialog } from "primereact/confirmdialog";
import { DataView } from "primereact/dataview";
import { ProgressSpinner } from "primereact/progressspinner";
import { Toast } from "primereact/toast";
import Section1 from "../components/section1";
import Section2 from "../components/section2";
import Section3 from "../components/section3";
import Section4 from "../components/section4";
import Section5 from "../components/section5";
import Section6 from "../components/section6";
import Section7 from "../components/section7";
import Section8 from "../components/section8";
import Section9 from "../components/section9";
import Section10 from "../components/section10";
import Section11 from "../components/section11";
import Section12 from "../components/section12";
import Section13 from "../components/section13";
import { BusinessHomeProfile } from "../../../models/businessHomeProfile";
import SubscribeForUnlimitedAccessDialog from "../../../components/general/SubscribeForUnlimitedAccessDialog";
import { RouterAddress } from "../../../constants/RouterAddress";
import { ROLE_TYPE_PROVIDER } from "../../../constants/RegisterConstants";
import { getErrorTitle } from "../../../constants/constants";
import Unauthorized from "../../../shared/pages/Unauthorized";
import WebsiteTemplateRenderer from "../../publishWeb/components/WebsiteTemplateRenderer";
import { DEFAULT_WEBSITE_TEMPLATE_KEY, WEBSITE_TEMPLATE_OPTIONS } from "../../publishWeb/components/websiteTemplates";
import { appConfirmDialogClasses, appMutedTextStyle } from "../../../components/general/uiSurfaceStyles";
import { dashboardSectionCardStyle } from "../../dashboard/pages/dashboardStyles";
import { getQrCodeDataUrl, getWebsiteUrlForHome } from "../../../utils/stringUtils";
import WebsiteInquiriesModal from "../../home/components/WebsiteInquiriesModal";
import WebsiteStatisticsModal from "../../home/components/WebsiteStatisticsModal";
import { useTour } from "../../../shared/tour/TourProvider";

const ManageWebsite = () => {
  const toast = React.useRef<Toast>(null);
  const location = useLocation();
  const searchParams = new URLSearchParams(location.search);
  const homeId = searchParams.get("homeId");
  const isPreview = searchParams.get("preview") === "true";
  const previewTemplateKey = searchParams.get("templateKey") || DEFAULT_WEBSITE_TEMPLATE_KEY;
  const { backendAPIStore, authStore } = useContext(MobXProviderContext);
  const userRoleType = authStore.getUserRoleType();
  const [loading, setLoading] = useState(false);
  const [publishLoading, setPublishLoading] = useState(false);
  const [resetLoading, setResetLoading] = useState(false);
  const [billingSubscriptionActive, setBillingSubscriptionActive] = useState<boolean>(false);
  const [showWebsitePublishLimitDialog, setShowWebsitePublishLimitDialog] = useState<boolean>(false);
  const [websitePublishLimitDialogMessage, setWebsitePublishLimitDialogMessage] = useState<string>("An active subscription is required to publish your website.");
  const [websiteTemplateCreditsAvailable, setWebsiteTemplateCreditsAvailable] = useState<number>(0);
  const [websiteTemplateUnlockedHomeIds, setWebsiteTemplateUnlockedHomeIds] = useState<string[]>([]);
  const [businessData, setBusinessData] = useState<BusinessHomeProfile | null>(null);
  const [data, setData] = useState<any[]>([]);
  const [apiFlag, setApiFlag] = useState(false);
  const [activeSectionKey, setActiveSectionKey] = useState("header");
  const [websiteQRCodeUrl, setWebsiteQRCodeUrl] = useState<string>("");
  const [copyUrl, setCopyUrl] = useState<string>("");
  const [copied, setCopied] = useState<boolean>(false);
  const [openWebsiteInquiriesModal, setOpenWebsiteInquiriesModal] = useState<boolean>(false);
  const [openWebsiteStatisticsModal, setOpenWebsiteStatisticsModal] = useState<boolean>(false);
  const [websiteInquiryFlag, setWebsiteInquiryFlag] = useState<boolean>(false);
  const [websiteInquiries, setWebsiteInquiries] = useState<any[]>([]);
  const [updatingWebsiteTemplate, setUpdatingWebsiteTemplate] = useState<string | null>(null);
  const [showEditorWorkspace, setShowEditorWorkspace] = useState<boolean>(false);
  const hasLoadedOnceRef = useRef(false);
  const editorWorkspaceRef = useRef<HTMLDivElement | null>(null);
  const { isStepActive, isActive: isTourActive } = useTour();

  useEffect(() => {
    const fetchData = async () => {
      try {
        if (!hasLoadedOnceRef.current) {
          setLoading(true);
        }
        await backendAPIStore.init();
        const userId = authStore.getUserId();
        if (userId) {
          const billingDetails = await backendAPIStore.getSubscription(userId);
          setBillingSubscriptionActive(!!billingDetails?.isSubscribed);
          setWebsiteTemplateCreditsAvailable(Number(billingDetails?.websiteTemplateCreditsAvailable || 0));
          setWebsiteTemplateUnlockedHomeIds(Array.isArray(billingDetails?.websiteTemplateUnlockedHomeIds) ? billingDetails.websiteTemplateUnlockedHomeIds : []);
        } else {
          setBillingSubscriptionActive(false);
          setWebsiteTemplateCreditsAvailable(0);
          setWebsiteTemplateUnlockedHomeIds([]);
        }
        const response = await backendAPIStore.getTemplatesDetails("DRAFT", homeId);
        setBusinessData(response?.businessDetails);
        setData(response?.Items || []);
        const websiteUrl = getWebsiteUrlForHome(response?.businessDetails);
        setCopyUrl(websiteUrl);
        if (response?.businessDetails?.isWebsiteActive) {
          const qrCodeUrl = await getQrCodeDataUrl(websiteUrl);
          setWebsiteQRCodeUrl(qrCodeUrl);
        } else {
          setWebsiteQRCodeUrl("");
        }
        hasLoadedOnceRef.current = true;
      } catch (error) {
        toast.current?.show({
          severity: "error",
          summary: getErrorTitle(error),
          detail: "Error fetching template details.",
          life: 3000,
        });
        console.error("Error fetching template details:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [authStore, backendAPIStore, homeId, apiFlag]);

  const headerData = data.find((obj: any) => obj.websiteId?.split("#").pop() === "HEADER");
  const bannerData = data.find((obj: any) => obj.websiteId?.split("#").pop() === "BANNER");
  const aboutData = data.find((obj: any) => obj.websiteId?.split("#").pop() === "ABOUT");
  const serviceData = data.find((obj: any) => obj.websiteId?.split("#").pop() === "SERVICE");
  const galleryData = data.find((obj: any) => obj.websiteId?.split("#").pop() === "GALLERY");
  const roomsData = data.find((obj: any) => obj.websiteId?.split("#").pop() === "ROOMS");
  const complianceData = data.find((obj: any) => obj.websiteId?.split("#").pop() === "COMPLIANCE");
  const careServicesData = data.find((obj: any) => obj.websiteId?.split("#").pop() === "CARE_SERVICES");
  const providerAboutData = data.find((obj: any) => obj.websiteId?.split("#").pop() === "PROVIDER_ABOUT");
  const admissionsData = data.find((obj: any) => obj.websiteId?.split("#").pop() === "ADMISSIONS");
  const admissionChecklistData = data.find((obj: any) => obj.websiteId?.split("#").pop() === "ADMISSION_CHECKLIST");

  const editorSections = useMemo(() => ([
    headerData ? { key: "header", label: "Header", note: "Brand and nav" } : null,
    businessData ? { key: "template", label: "Template", note: "Theme and publish" } : null,
    bannerData ? { key: "banner", label: "Banner", note: "Hero section" } : null,
    aboutData ? { key: "about", label: "About", note: "Story and intro" } : null,
    serviceData ? { key: "service", label: "Services", note: "Core care" } : null,
    galleryData ? { key: "gallery", label: "Gallery", note: "Photos and media" } : null,
    roomsData ? { key: "rooms", label: "Rooms", note: "Optional page" } : null,
    complianceData ? { key: "compliance", label: "Compliance", note: "Optional page" } : null,
    careServicesData ? { key: "care-services", label: "Care Services", note: "Optional page" } : null,
    providerAboutData ? { key: "provider-about", label: "Provider About", note: "Optional page" } : null,
    admissionsData ? { key: "admissions", label: "Admissions", note: "Optional page" } : null,
    admissionChecklistData ? { key: "admission-checklist", label: "Checklist", note: "Optional page" } : null,
    businessData ? { key: "contact", label: "Contact", note: "Map and inquiry" } : null,
  ].filter(Boolean)) as { key: string; label: string; note: string }[], [
    headerData,
    businessData,
    bannerData,
    aboutData,
    serviceData,
    galleryData,
    roomsData,
    complianceData,
    careServicesData,
    providerAboutData,
    admissionsData,
    admissionChecklistData,
  ]);

  const visibleEditorSections = useMemo(() => {
    if (editorSections.length <= 5) {
      return editorSections;
    }

    const activeIndex = Math.max(0, editorSections.findIndex((section) => section.key === activeSectionKey));
    const start = Math.max(0, Math.min(activeIndex - 2, editorSections.length - 5));
    return editorSections.slice(start, start + 5);
  }, [editorSections, activeSectionKey]);

  const firstVisibleSectionIndex = visibleEditorSections.length > 0
    ? editorSections.findIndex((section) => section.key === visibleEditorSections[0].key)
    : 0;
  const lastVisibleSectionIndex = visibleEditorSections.length > 0
    ? editorSections.findIndex((section) => section.key === visibleEditorSections[visibleEditorSections.length - 1].key)
    : -1;
  const hasPreviousHiddenSections = firstVisibleSectionIndex > 0;
  const hasNextHiddenSections = lastVisibleSectionIndex >= 0 && lastVisibleSectionIndex < editorSections.length - 1;

  useEffect(() => {
    if (editorSections.length === 0) {
      return;
    }

    const handleScroll = () => {
      let currentKey = editorSections[0].key;

      for (const section of editorSections) {
        const element = document.getElementById(`editor-section-${section.key}`);
        if (!element) {
          continue;
        }

        const rect = element.getBoundingClientRect();
        if (rect.top <= 180) {
          currentKey = section.key;
        }
      }

      setActiveSectionKey(currentKey);
    };

    handleScroll();
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [editorSections]);

  useEffect(() => {
    const fetchWebsiteInquiries = async () => {
      if (!homeId) {
        return;
      }
      try {
        await backendAPIStore.init();
        const response = await backendAPIStore.listWebsiteInquiries(homeId, null);
        if (response) {
          setWebsiteInquiries(response.inquiries || []);
        }
      } catch (error: any) {
        toast.current?.show({
          severity: "error",
          summary: getErrorTitle(error),
          detail: error?.message || "Unable to load website inquiries.",
          life: 3000,
        });
      }
    };

    void fetchWebsiteInquiries();
  }, [backendAPIStore, homeId, websiteInquiryFlag]);

  useEffect(() => {
    const shouldShowEditorForTour = isTourActive && (
      isStepActive("website-edit-content")
      || isStepActive("website-theme-picker")
      || isStepActive("website-editor-timeline")
      || isStepActive("website-publish")
    );

    if (shouldShowEditorForTour && !showEditorWorkspace) {
      setShowEditorWorkspace(true);
      window.setTimeout(() => {
        if (editorWorkspaceRef.current) {
          const top = editorWorkspaceRef.current.getBoundingClientRect().top + window.scrollY - 110;
          window.scrollTo({ top, behavior: "smooth" });
        }
      }, 150);
    }
  }, [isStepActive, isTourActive, showEditorWorkspace]);

  const scrollToEditorSection = (sectionKey: string) => {
    const element = document.getElementById(`editor-section-${sectionKey}`);
    if (!element) {
      return;
    }

    const top = element.getBoundingClientRect().top + window.scrollY - 140;
    window.scrollTo({ top, behavior: "smooth" });
    setActiveSectionKey(sectionKey);
  };

  const handleCopy = async () => {
    if (!copyUrl) {
      return;
    }

    if (navigator.clipboard?.writeText) {
      await navigator.clipboard.writeText(copyUrl);
    } else {
      const tempInput = document.createElement("input");
      tempInput.value = copyUrl;
      document.body.appendChild(tempInput);
      tempInput.select();
      document.execCommand("copy");
      document.body.removeChild(tempInput);
    }

    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleStatisticsClick = () => {
    setOpenWebsiteStatisticsModal(true);
  };

  const handleInquiriesClick = () => {
    setOpenWebsiteInquiriesModal(true);
  };

  const openEditorWorkspace = () => {
    setShowEditorWorkspace(true);
    window.setTimeout(() => {
      if (editorWorkspaceRef.current) {
        const top = editorWorkspaceRef.current.getBoundingClientRect().top + window.scrollY - 110;
        window.scrollTo({ top, behavior: "smooth" });
      }
    }, 120);
  };

  const handleTemplatePreviewClick = (templateKey: string) => {
    window.open(RouterAddress.getManageWebsitePreviewPath(homeId!, templateKey), "_blank", "noopener,noreferrer");
  };

  const handleTemplateSelection = async (templateKey: string) => {
    try {
      setUpdatingWebsiteTemplate(templateKey);
      await backendAPIStore.init();
      await backendAPIStore.updateWebsiteTemplateSelection({
        homeId: homeId!,
        templateKey,
      });
      setApiFlag((prev) => !prev);
      toast.current?.show({
        severity: "success",
        summary: "Template selected",
        detail: "Your draft website template is ready to preview and publish.",
        life: 3000,
      });
      scrollToEditorSection("template");
    } catch (error: any) {
      toast.current?.show({
        severity: "error",
        summary: getErrorTitle(error),
        detail: error?.message || "Unable to update template selection.",
        life: 3000,
      });
    } finally {
      setUpdatingWebsiteTemplate(null);
    }
  };

  const disableWebsite = async (flag: boolean | undefined) => {
    if (flag === undefined || !homeId) return;
    confirmDialog({
      message: flag
        ? "Your public home website will become visible to visitors again."
        : "Visitors will no longer be able to access the public website until you reactivate it.",
      header: flag ? "Bring website online?" : "Take website offline?",
      icon: "pi pi-info-circle",
      ...appConfirmDialogClasses,
      accept: async () => {
        try {
          await backendAPIStore.init();
          await backendAPIStore.enableDisableTemplate(homeId, flag);
          setApiFlag((prev) => !prev);
        } catch (error: any) {
          toast.current?.show({
            severity: "error",
            summary: getErrorTitle(error),
            detail: error?.message || "Unable to update website visibility.",
            life: 3000,
          });
        }
      },
      reject: () => { },
    });
  };

  const handlePublish = async () => {
    if (!homeId) {
      return;
    }

    if (!billingSubscriptionActive) {
      setWebsitePublishLimitDialogMessage("An active subscription is required to publish your website.");
      setShowWebsitePublishLimitDialog(true);
      return;
    }

    try {
      setPublishLoading(true);
      await backendAPIStore.init();
      const response = await backendAPIStore.publishTemplate({
        homeId,
        type: "PUBLISH",
      });
      if (response) {
        await backendAPIStore.enableDisableTemplate(homeId, true);
      }
      toast.current?.show({
        severity: "success",
        summary: "Website published",
        detail: "Your latest website edits are now live.",
        life: 3000,
      });
      setApiFlag((prev) => !prev);
    } catch (error: any) {
      toast.current?.show({
        severity: "error",
        summary: getErrorTitle(error),
        detail: error?.message || "Unable to publish website.",
        life: 3000,
      });
    } finally {
      setPublishLoading(false);
    }
  };

  const handleResetToDefaults = () => {
    if (!homeId) {
      return;
    }

    confirmDialog({
      message: (
        <div style={{ display: "grid", gap: "0.5rem", lineHeight: 1.7 }}>
          <div style={{ fontWeight: 700, color: "#0f172a" }}>Reset website draft to defaults?</div>
          <div style={{ color: "#475569" }}>
            This will replace your current draft content and draft template selection with the default website setup.
            Your published website will stay unchanged until you publish again.
          </div>
        </div>
      ),
      header: "Reset Website Draft",
      icon: "pi pi-refresh",
      ...appConfirmDialogClasses,
      accept: async () => {
        try {
          setResetLoading(true);
          await backendAPIStore.init();
          await backendAPIStore.resetWebsiteDraft({ homeId });
          toast.current?.show({
            severity: "success",
            summary: "Draft reset",
            detail: "Your website draft has been restored to the default content.",
            life: 3000,
          });
          setApiFlag((prev) => !prev);
        } catch (error: any) {
          toast.current?.show({
            severity: "error",
            summary: getErrorTitle(error),
            detail: error?.message || "Unable to reset website draft.",
            life: 3000,
          });
        } finally {
          setResetLoading(false);
        }
      },
      reject: () => { },
    });
  };

  if (userRoleType && userRoleType !== ROLE_TYPE_PROVIDER) {
    return <Unauthorized />;
  }

  const websiteEditorSections = (
    <div className="flex flex-wrap align-items-center justify-content-between">
      {headerData && (
        <div id="editor-section-header" style={{ width: "100%" }}>
          <Section1
            key={headerData.websiteId}
            data={headerData}
            flag={apiFlag}
            setFlag={setApiFlag}
            homeId={homeId}
            businessData={businessData}
          />
        </div>
      )}
      {businessData && <div id="editor-section-template" style={{ width: "100%" }}><Section7 businessData={businessData} /></div>}
      {bannerData && <div id="editor-section-banner" style={{ width: "100%" }}><Section2 key={bannerData.websiteId} data={bannerData} flag={apiFlag} setFlag={setApiFlag} homeId={homeId} /></div>}
      {aboutData && <div id="editor-section-about" style={{ width: "100%" }}><Section3 key={aboutData.websiteId} data={aboutData} flag={apiFlag} setFlag={setApiFlag} homeId={homeId} /></div>}
      {serviceData && <div id="editor-section-service" style={{ width: "100%" }}><Section4 key={serviceData.websiteId} data={serviceData} flag={apiFlag} setFlag={setApiFlag} homeId={homeId} /></div>}
      {galleryData && <div id="editor-section-gallery" style={{ width: "100%" }}><Section5 key={galleryData.websiteId} data={galleryData} flag={apiFlag} setFlag={setApiFlag} homeId={homeId} /></div>}
      {roomsData && <div id="editor-section-rooms" style={{ width: "100%" }}><Section8 key={roomsData.websiteId} data={roomsData} flag={apiFlag} setFlag={setApiFlag} homeId={homeId} /></div>}
      {complianceData && <div id="editor-section-compliance" style={{ width: "100%" }}><Section9 key={complianceData.websiteId} data={complianceData} flag={apiFlag} setFlag={setApiFlag} homeId={homeId} /></div>}
      {careServicesData && <div id="editor-section-care-services" style={{ width: "100%" }}><Section10 key={careServicesData.websiteId} data={careServicesData} flag={apiFlag} setFlag={setApiFlag} homeId={homeId} /></div>}
      {providerAboutData && <div id="editor-section-provider-about" style={{ width: "100%" }}><Section11 key={providerAboutData.websiteId} data={providerAboutData} flag={apiFlag} setFlag={setApiFlag} homeId={homeId} /></div>}
      {admissionsData && <div id="editor-section-admissions" style={{ width: "100%" }}><Section12 key={admissionsData.websiteId} data={admissionsData} flag={apiFlag} setFlag={setApiFlag} homeId={homeId} /></div>}
      {admissionChecklistData && <div id="editor-section-admission-checklist" style={{ width: "100%" }}><Section13 key={admissionChecklistData.websiteId} data={admissionChecklistData} flag={apiFlag} setFlag={setApiFlag} homeId={homeId} /></div>}
      {businessData && <div id="editor-section-contact" style={{ width: "100%" }}><Section6 data={businessData} headerData={headerData} /></div>}
    </div>
  );

  const currentDraftTemplateKey = businessData?.websiteDraftTemplateKey || DEFAULT_WEBSITE_TEMPLATE_KEY;
  const currentPublishedTemplateKey = businessData?.websitePublishedTemplateKey || DEFAULT_WEBSITE_TEMPLATE_KEY;
  const websiteTemplateItems = WEBSITE_TEMPLATE_OPTIONS.map((option) => {
    const isDraft = currentDraftTemplateKey === option.key;
    const isLive = currentPublishedTemplateKey === option.key;
    const requiresPremiumAccess = option.key !== DEFAULT_WEBSITE_TEMPLATE_KEY;
    const hasTemplateAccessForHome = billingSubscriptionActive || websiteTemplateUnlockedHomeIds.includes(homeId || "");

    return {
      ...option,
      isDraft,
      isLive,
      requiresPremiumAccess,
      hasTemplateAccessForHome,
    };
  });

  if (isPreview && previewTemplateKey !== DEFAULT_WEBSITE_TEMPLATE_KEY) {
    return (
      <div style={{ background: "#f8fafc", minHeight: "100vh", padding: "1rem" }}>
        <Toast ref={toast} />
        {loading ? (
          <div className="col-12 text-center d-flex justify-content-center align-items-center">
            <ProgressSpinner role="status" style={{ color: "#185DA2" }} />
          </div>
        ) : (
          <div style={{ display: "grid", gap: "1rem" }}>
            <div
              style={{
                maxWidth: "1180px",
                margin: "0 auto",
                width: "100%",
                background: "#ffffff",
                border: "1px solid #e2e8f0",
                borderRadius: "18px",
                padding: "1rem 1.1rem",
              }}
            >
              <div style={{ fontWeight: 700, color: "#0f172a" }}>Template Preview</div>
              <div style={{ color: "#64748b", lineHeight: 1.7, marginTop: "0.35rem" }}>
                This preview uses your current draft website content with the selected template. Publish from Home Profile when you are ready to make it live.
              </div>
            </div>
            <WebsiteTemplateRenderer
              templateKey={previewTemplateKey}
              businessData={businessData}
              headerData={headerData || null}
              bannerData={bannerData || null}
              aboutData={aboutData || null}
              serviceData={serviceData || null}
              galleryData={galleryData || null}
              roomsData={roomsData || null}
              complianceData={complianceData || null}
              careServicesData={careServicesData || null}
              providerAboutData={providerAboutData || null}
              admissionsData={admissionsData || null}
              admissionChecklistData={admissionChecklistData || null}
              isPreview
            />
          </div>
        )}
      </div>
    );
  }

  return (
    <div
      className="container-fluid"
      id="manage-website-container"
      style={{
        background: "linear-gradient(180deg, #f7fbff 0%, #eef5fb 100%)",
        padding: "0 0 3rem",
      }}
    >
      <Toast ref={toast} />
      <SubscribeForUnlimitedAccessDialog
        visible={showWebsitePublishLimitDialog}
        onHide={() => setShowWebsitePublishLimitDialog(false)}
        title="More Website Publish Access Needed"
        message={websitePublishLimitDialogMessage}
        billingPath={RouterAddress.BILLING}
        billingButtonLabel="Subscription"
      />
      <WebsiteInquiriesModal
        inquiries={websiteInquiries}
        isOpen={openWebsiteInquiriesModal}
        toggleModal={setOpenWebsiteInquiriesModal}
        flag={websiteInquiryFlag}
        setFlag={setWebsiteInquiryFlag}
      />
      <WebsiteStatisticsModal
        homeId={homeId || undefined}
        isOpen={openWebsiteStatisticsModal}
        toggleModal={setOpenWebsiteStatisticsModal}
      />
      {loading ? (
        <div className="col-12 text-center d-flex justify-content-center align-items-center">
          <ProgressSpinner role="status" style={{ color: "#185DA2" }} />
        </div>
      ) : (
        <>
          <div style={{ padding: "1rem" }}>
            <div style={{ maxWidth: "1180px", margin: "0 auto" }}>
              <div className="grid">
                <div className="col-12 lg:col-7">
                  <div style={{ ...dashboardSectionCardStyle, padding: "1.25rem", background: "#ffffff" }} className="h-full" data-tour="website-overview">
                    <div className="grid">
                      <div className="col-12 md:col-6" data-tour="website-status-url">
                        <div style={{ fontWeight: 700, color: "#0f172a" }}>Website Status</div>
                        <div
                          className="mt-3"
                          style={{
                            display: "inline-flex",
                            alignItems: "center",
                            gap: "0.5rem",
                            borderRadius: "999px",
                            padding: "0.55rem 0.95rem",
                            background: businessData?.isWebsiteActive ? "#ecfdf3" : "#fef2f2",
                            color: businessData?.isWebsiteActive ? "#047857" : "#b91c1c",
                            fontWeight: 700,
                          }}
                        >
                          <i className={`pi ${businessData?.isWebsiteActive ? "pi-check-circle" : "pi-times-circle"}`} />
                          <span>{businessData?.isWebsiteActive ? "Live" : "Offline"}</span>
                        </div>
                      </div>
                      <div className="col-12 md:col-6" data-tour="website-status-url">
                        <div style={{ fontWeight: 700, color: "#0f172a" }}>Public URL</div>
                        {businessData?.isWebsiteActive ? (
                          <div className="mt-3 flex flex-wrap align-items-center gap-2">
                            <a target="_blank" rel="noopener noreferrer" href={copyUrl}>{copyUrl}</a>
                            <Button icon="pi pi-copy" outlined rounded severity="warning" tooltip="Copy URL" onClick={() => { void handleCopy(); }} />
                            {copied ? <span style={{ color: "#15803d", fontWeight: 600 }}>Copied</span> : null}
                          </div>
                        ) : (
                          <div className="mt-3" style={appMutedTextStyle}>
                            Publish the site to generate and share a public URL.
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="grid mt-3">
                      <div className="col-12 md:col-6">
                        <div style={{ padding: "0.25rem 0" }}>
                          <div style={{ fontWeight: 700, color: "#0f172a" }}>Website Controls</div>
                          <div className="mt-3 flex flex-column gap-2" style={appMutedTextStyle}>
                            <div><strong style={{ color: "#0f172a" }}>Themes:</strong> choose a draft theme, preview it, and publish when ready.</div>
                            <div><strong style={{ color: "#0f172a" }}>Analytics:</strong> review traffic, inquiries, and engagement.</div>
                            <div><strong style={{ color: "#0f172a" }}>QR Code:</strong> share the public website quickly once it is live.</div>
                          </div>
                        </div>
                      </div>
                      <div className="col-12 md:col-6" data-tour="website-quick-actions">
                        <div style={{ padding: "0.25rem 0" }}>
                          <div style={{ fontWeight: 700, color: "#0f172a" }}>Quick actions</div>
                          <div className="mt-3 flex flex-wrap gap-2">
                            <Button label="Edit Template" className="p-button-outlined" rounded data-tour="website-edit-template" onClick={openEditorWorkspace} />
                            <Button label={businessData?.isWebsiteActive ? "Republish Website" : "Publish Website"} className="p-button-outlined" severity="warning" rounded onClick={handlePublish} loading={publishLoading} data-tour="website-publish" />
                            <Button label="View Inquiries" className="p-button-outlined" severity="warning" rounded onClick={handleInquiriesClick} data-tour="website-view-inquiries" />
                            <Button label="View Analytics" className="p-button-outlined" severity="warning" rounded onClick={handleStatisticsClick} data-tour="website-view-analytics" />
                            {businessData?.isWebsiteActive ? (
                              <Button label="Take Offline" className="p-button-outlined" severity="danger" rounded onClick={() => void disableWebsite(false)} />
                            ) : null}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-12 lg:col-5">
                  <div style={{ display: "grid", gap: "1rem" }}>
                    <div style={{ ...dashboardSectionCardStyle, padding: "1rem", background: "#ffffff" }} data-tour="website-theme-picker">
                      <div className="flex flex-wrap align-items-start justify-content-between gap-2">
                        <div>
                          <div style={{ fontWeight: 700, color: "#0f172a" }}>Choose Website Theme</div>
                          <div className="mt-1" style={{ color: "#64748b", lineHeight: 1.6, fontSize: "0.92rem" }}>
                            Pick a draft theme, preview it, then publish when ready.
                          </div>
                        </div>
                        <div
                          style={{
                            background: "#f8fafc",
                            border: "1px solid #e2e8f0",
                            borderRadius: "999px",
                            padding: "0.45rem 0.8rem",
                            color: "#475569",
                            fontSize: "0.84rem",
                            fontWeight: 600,
                            whiteSpace: "nowrap",
                          }}
                        >
                          Credits: <strong>{websiteTemplateCreditsAvailable}</strong>
                        </div>
                      </div>
                      <div className="mt-3">
                        <DataView
                          value={websiteTemplateItems}
                          layout="list"
                          itemTemplate={(option: any) => (
                            <div
                              key={option.key}
                              style={{
                                border: `1px solid ${option.isDraft ? option.accentColor : "#e2e8f0"}`,
                                borderRadius: "14px",
                                padding: "0.8rem 0.9rem",
                                background: option.isDraft ? `${option.accentColor}12` : "#ffffff",
                              }}
                              className="mb-2"
                            >
                              <div className="flex flex-column lg:flex-row lg:align-items-center lg:justify-content-between gap-3">
                                <div style={{ flex: 1, minWidth: 0 }}>
                                  <div className="flex flex-wrap align-items-center gap-2">
                                    <div style={{ fontWeight: 700, color: "#0f172a", fontSize: "0.96rem" }}>{option.name}</div>
                                    {option.isLive && <span style={{ background: "#ecfdf3", color: "#047857", borderRadius: "999px", padding: "0.2rem 0.55rem", fontSize: "0.74rem", fontWeight: 700 }}>Live</span>}
                                    {option.isDraft && <span style={{ background: "#eff6ff", color: "#185DA2", borderRadius: "999px", padding: "0.2rem 0.55rem", fontSize: "0.74rem", fontWeight: 700 }}>Selected</span>}
                                    {option.requiresPremiumAccess && !option.hasTemplateAccessForHome && (
                                      <span style={{ background: "#fff7ed", color: "#b45309", borderRadius: "999px", padding: "0.2rem 0.55rem", fontSize: "0.74rem", fontWeight: 700 }}>
                                        Premium
                                      </span>
                                    )}
                                  </div>
                                  <div className="mt-1" style={{ color: "#64748b", lineHeight: 1.5, fontSize: "0.85rem" }}>
                                    {option.description}
                                  </div>
                                  <div className="mt-2" style={{ color: option.accentColor, fontWeight: 700, fontSize: "0.8rem" }}>
                                    {option.layoutLabel}
                                  </div>
                                </div>
                                <div className="flex flex-wrap gap-2 lg:justify-content-end">
                                  <Button label="Preview" rounded outlined size="small" onClick={() => handleTemplatePreviewClick(option.key)} />
                                  <Button
                                    label={option.isDraft ? "Selected" : "Use"}
                                    rounded
                                    severity="warning"
                                    size="small"
                                    disabled={option.isDraft}
                                    loading={updatingWebsiteTemplate === option.key}
                                    onClick={() => { void handleTemplateSelection(option.key); }}
                                  />
                                </div>
                              </div>
                            </div>
                          )}
                        />
                      </div>
                    </div>
                    <div style={{ ...dashboardSectionCardStyle, padding: "1.25rem", background: "#ffffff", height: "100%" }} className="flex flex-column align-items-center justify-content-center gap-3 text-center" data-tour="website-qr-code">
                      {businessData?.isWebsiteActive && websiteQRCodeUrl ? (
                        <>
                          <img src={websiteQRCodeUrl} alt="QR Code" height={200} width={200} />
                          <div style={{ fontWeight: 700, color: "#0f172a" }}>Website QR Code</div>
                          <div style={appMutedTextStyle}>
                            Share this QR code on printed materials so visitors can reach your site quickly.
                          </div>
                        </>
                      ) : (
                        <>
                          <i className="pi pi-qrcode" style={{ fontSize: "2rem", color: "#94a3b8" }} />
                          <div style={{ fontWeight: 700, color: "#0f172a" }}>QR code available after publish</div>
                          <div style={appMutedTextStyle}>
                            Once the website is live, a shareable QR code will appear here.
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {showEditorWorkspace ? (
            <>
              <div
                ref={editorWorkspaceRef}
                style={{
                  position: "sticky",
                  top: "0.75rem",
                  zIndex: 12,
                  padding: "1rem 1rem 0",
                }}
              >
                <div
                  data-tour="website-editor-timeline"
                  style={{
                    maxWidth: "1180px",
                    margin: "0 auto",
                    background: "rgba(255,255,255,0.96)",
                    border: "1px solid #dbe7f3",
                    borderRadius: "24px",
                    boxShadow: "0 18px 38px rgba(15,61,99,0.1)",
                    padding: "0.9rem 1rem",
                    backdropFilter: "blur(10px)",
                  }}
                >
                  <div className="flex flex-wrap align-items-center justify-content-between gap-2">
                    <div>
                      <div style={{ fontWeight: 800, color: "#0f172a" }}>Website Editor Timeline</div>
                      <div style={{ color: "#64748b", fontSize: "0.9rem", marginTop: "0.2rem" }}>
                        Track the sections available on this website and jump straight to the one you are editing.
                      </div>
                    </div>
                    <div style={{ color: "#475569", fontSize: "0.84rem", fontWeight: 700 }}>
                      Active: {editorSections.find((section) => section.key === activeSectionKey)?.label || "Header"}
                    </div>
                  </div>
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      flexWrap: "wrap",
                      gap: "0.6rem",
                      paddingTop: "0.95rem",
                      paddingBottom: "0.15rem",
                    }}
                  >
                    {hasPreviousHiddenSections ? (
                      <div
                        style={{
                          color: "#64748b",
                          fontWeight: 800,
                          fontSize: "1rem",
                          padding: "0.5rem 0.35rem",
                          userSelect: "none",
                        }}
                        aria-hidden="true"
                      >
                        ...
                      </div>
                    ) : null}
                    {visibleEditorSections.map((section) => {
                      const isActive = section.key === activeSectionKey;
                      const sectionIndex = editorSections.findIndex((item) => item.key === section.key);
                      return (
                        <button
                          key={section.key}
                          type="button"
                          onClick={() => scrollToEditorSection(section.key)}
                          data-telemetry-action={`website_editor_jump_${section.key}`}
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "0.7rem",
                            borderRadius: "999px",
                            border: isActive ? "1px solid #185DA2" : "1px solid #dbe7f3",
                            background: isActive ? "#eff6ff" : "#ffffff",
                            padding: "0.5rem 0.75rem",
                            cursor: "pointer",
                            flex: "0 1 auto",
                            minWidth: 0,
                          }}
                        >
                          <div
                            style={{
                              width: "1.65rem",
                              height: "1.65rem",
                              borderRadius: "999px",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "center",
                              background: isActive ? "#185DA2" : "#e2e8f0",
                              color: isActive ? "#ffffff" : "#475569",
                              fontWeight: 800,
                              fontSize: "0.76rem",
                              flex: "0 0 auto",
                            }}
                          >
                            {sectionIndex + 1}
                          </div>
                          <div style={{ textAlign: "left" }}>
                            <div style={{ color: "#0f172a", fontWeight: 700, fontSize: "0.86rem", whiteSpace: "nowrap" }}>{section.label}</div>
                          </div>
                        </button>
                      );
                    })}
                    {hasNextHiddenSections ? (
                      <div
                        style={{
                          color: "#64748b",
                          fontWeight: 800,
                          fontSize: "1rem",
                          padding: "0.5rem 0.35rem",
                          userSelect: "none",
                        }}
                        aria-hidden="true"
                      >
                        ...
                      </div>
                    ) : null}
                  </div>
                </div>
              </div>
              {websiteEditorSections}
              <div
                style={{
                  position: "sticky",
                  bottom: "1rem",
                  display: "flex",
                  justifyContent: "flex-end",
                  padding: "0 1rem",
                  marginTop: "1rem",
                  zIndex: 10,
                }}
              >
                <div
                  style={{
                    background: "rgba(255,255,255,0.96)",
                    border: "1px solid #dbe7f3",
                    borderRadius: "999px",
                    boxShadow: "0 16px 34px rgba(15,61,99,0.12)",
                    padding: "0.65rem 0.75rem",
                    display: "flex",
                    alignItems: "center",
                    gap: "0.75rem",
                  }}
                >
                  <div style={{ color: "#475569", fontSize: "0.9rem", fontWeight: 600 }}>
                    {billingSubscriptionActive
                      ? "Publish your latest website edits"
                      : "Preview and edit your website draft. Publishing requires an active subscription."}
                  </div>
                  <Button
                    label="Reset To Defaults"
                    severity="secondary"
                    outlined
                    rounded
                    loading={resetLoading}
                    onClick={handleResetToDefaults}
                    data-telemetry-action="website_reset_to_defaults"
                  />
                  <Button
                    label={businessData?.isWebsiteActive ? "Republish Website" : "Publish Website"}
                    severity="warning"
                    rounded
                    data-tour="website-publish"
                    loading={publishLoading}
                    onClick={handlePublish}
                    data-telemetry-action={businessData?.isWebsiteActive ? "website_republish" : "website_publish"}
                  />
                </div>
              </div>
            </>
          ) : null}
        </>
      )}
    </div>
  );
};

export default ManageWebsite;
